import { Component, ElementRef, Input, Output, EventEmitter, HostListener, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ColunaDesempenho, NotaView } from '../models/nota-types';
import { ConfiguracaoAvaliacao } from '../../../core/config/services/avaliacao.service';

@Component({
  selector: 'app-input-nota',
  templateUrl: './input-nota.component.html',
  styleUrls: ['./input-nota.component.scss']
})
export class InputNotaComponent implements OnChanges, OnDestroy {
  private static readonly TIPOS_OCULTA_NOTA_EXAME = ['REPROVADO', 'APROVADO'];
  private isEditandoNumerico = false;
  private autoSaveTimer: ReturnType<typeof setTimeout> | null = null;

  @Input() nota: NotaView | undefined;
  @Input() coluna: ColunaDesempenho | null = null;
  @Input() configuracaoAvaliacao: ConfiguracaoAvaliacao = {};
  @Input() disabled = false;
  @Input() mostrarParecer = false;
  @Input() mostrarCha = false;
  @Input() usarEditavelAnual = false;
  @Input() bloquearChaExameFinal = false;

  @Output() notaSalva = new EventEmitter<NotaView>();
  @Output() openParecer = new EventEmitter<NotaView>();
  @Output() openCha = new EventEmitter<NotaView>();
  @Output() openAvaliacaoDescritiva = new EventEmitter<NotaView>();

  constructor(private hostRef: ElementRef<HTMLElement>) {}

  conceitoDropdownAberto = false;

  @HostListener('document:click')
  onDocumentClick(): void {
    this.conceitoDropdownAberto = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['nota']) {
      this.conceitoDropdownAberto = false;
      this.clearAutoSaveTimer();
    }
  }

  ngOnDestroy(): void {
    this.clearAutoSaveTimer();
  }

  getModoAvaliacao(): string {
    const nota = this.nota;
    if (!nota) {
      return 'NUMERICO';
    }

    const enturmacaoExt = nota.enturmacao as {
      avaliacaoDescritiva?: boolean;
      matricula?: { avaliacaoDescritiva?: boolean };
    } | undefined;
    if (enturmacaoExt?.avaliacaoDescritiva || enturmacaoExt?.matricula?.avaliacaoDescritiva) {
      return 'DESCRITIVO';
    }

    if (nota.avaliavel?.modoAvaliacao) {
      return String(nota.avaliavel.modoAvaliacao);
    }

    const modoColuna = this.resolveModoAvaliacaoColuna(this.coluna);
    if (modoColuna) {
      return modoColuna;
    }

    if (nota.$$modoAvaliacao && nota.$$modoAvaliacao !== 'NUMERICO') {
      return nota.$$modoAvaliacao;
    }

    if (nota.notaDescritiva) {
      return 'DESCRITIVO';
    }

    if (nota.notaConceito || (nota.$$nota && typeof nota.$$nota === 'object')) {
      return 'CONCEITO_NUMERICO';
    }

    return 'NUMERICO';
  }

  isModoNumerico(): boolean {
    return this.getModoAvaliacao() === 'NUMERICO';
  }

  isModoConceito(): boolean {
    const modo = this.getModoAvaliacao();
    return modo === 'CONCEITO' || modo === 'CONCEITO_NUMERICO';
  }

  isModoDescritivo(): boolean {
    return this.getModoAvaliacao() === 'DESCRITIVO';
  }

  getNotaNumericaValue(): string {
    const nota = this.nota;
    if (!nota) {
      return '';
    }
    if (nota.$$nota !== null && nota.$$nota !== undefined) {
      if (nota.$$nota === '') {
        return '';
      }
      if (typeof nota.$$nota === 'object') {
        return '';
      }
      if (this.isEditandoNumerico) {
        return String(nota.$$nota);
      }
      if (this.isModoNumerico()) {
        return this.formatarNotaNumericaConformeConfiguracao(nota.$$nota);
      }
      return String(nota.$$nota);
    }
    if (nota.notaNumerica !== undefined && nota.notaNumerica !== null) {
      return this.isModoNumerico()
        ? this.formatarNotaNumericaConformeConfiguracao(nota.notaNumerica)
        : String(nota.notaNumerica);
    }
    return '';
  }

  getConceitoOptions(): Array<Record<string, unknown> & { sigla: string; descricao?: string }> {
    const configuracao = this.configuracaoAvaliacao as Record<string, unknown>;
    const modo = this.getModoAvaliacao();
    const source =
      modo === 'CONCEITO_NUMERICO'
        ? this.getConceitosNumericos(configuracao)
        : (
            (configuracao['avaliacaoConceito'] as { conceitos?: Array<Record<string, unknown>> } | undefined)
              ?.conceitos || []
          );

    return source
      .map((item) => ({
        ...item,
        sigla: this.resolveConceitoSigla(item),
        descricao: this.resolveConceitoDescricao(item)
      }))
      .filter((item) => !!(item.sigla || item.descricao));
  }

  getConceitoOptionLabel(
    conceito: { sigla?: string; descricao?: string } | null | undefined,
    index?: number
  ): string {
    const sigla = this.getConceitoOptionSigla(conceito) || this.getSiglaAlfabeticaPorIndice(index ?? -1);
    const descricao = String(conceito?.descricao || '').trim();
    return sigla && descricao ? `${sigla} - ${descricao}` : sigla || descricao;
  }

  getConceitoPrimeiraLetraSelecionada(): string {
    if (!this.nota) {
      return '';
    }
    const valorBruto = this.getTextoConceitoSelecionado();
    const primeiraLetra = this.extrairPrimeiraLetraConceito(valorBruto);
    if (primeiraLetra) {
      return primeiraLetra;
    }
    return this.extrairPrimeiraLetraConceito(this.getConceitoSiglaSelecionada());
  }

  hasConteudoDescritivo(): boolean {
    return !!String(this.nota?.notaDescritiva || this.nota?.$$nota || '').trim();
  }

  hasConteudoParecer(): boolean {
    const nota = this.nota as (NotaView & { parecer?: string; parecerDetalhadoConteudo?: string }) | undefined;
    return !!String(nota?.parecerDetalhadoConteudo || nota?.parecer || '').trim();
  }

  onInputChange(event: Event): void {
    const nota = this.nota;
    if (!nota) {
      return;
    }
    const input = event.target as HTMLInputElement;
    if (this.isModoNumerico()) {
      const valorComMascara = this.aplicarMascaraNumericaTempoReal(input.value);
      input.value = valorComMascara;
      nota.$$nota = valorComMascara;
    } else {
      nota.$$nota = input.value;
    }
    nota.$$mensagemValidacao = null;
    nota.$$hasChanged = true;
    this.scheduleAutoSave();
  }

  onNumericFocus(): void {
    this.isEditandoNumerico = true;
  }

  onNumericKeyDown(event: KeyboardEvent): void {
    const tecla = event.key;
    const teclasPermitidas = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End', 'Enter'];

    if (event.ctrlKey || event.metaKey) {
      return;
    }

    if (teclasPermitidas.includes(tecla)) {
      return;
    }

    if (tecla === 'e' || tecla === 'E' || tecla === '+' || tecla === '-') {
      event.preventDefault();
      return;
    }

    if (tecla === '.' || tecla === ',') {
      const valorAtual = this.getNotaNumericaValue();
      if (valorAtual.includes('.') || valorAtual.includes(',')) {
        event.preventDefault();
      }
      return;
    }

    if (!/[0-9]/.test(tecla)) {
      event.preventDefault();
    }
  }

  getMaxLengthNumerico(): number | null {
    const limiteDigitos = this.getLimiteDigitosNumerico();
    if (!limiteDigitos) {
      return null;
    }

    const casasDecimais = this.getQuantidadeCasasDecimais();
    if (casasDecimais <= 0) {
      return limiteDigitos;
    }

    const limiteInteiro = Math.max(1, limiteDigitos - casasDecimais);
    return limiteInteiro + 1 + casasDecimais;
  }

  onBlur(): void {
    this.clearAutoSaveTimer();
    this.emitSaveIfNeeded(true);
    this.isEditandoNumerico = false;
  }

  onConceitoDropdownClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.conceitoDropdownAberto = !this.conceitoDropdownAberto;
  }

  selecionarConceito(valor: unknown, event?: MouseEvent): void {
    event?.stopPropagation();
    const nota = this.nota;
    if (!nota) {
      return;
    }
    nota.$$nota = this.normalizeNotaSelectValue(valor);
    nota.$$hasChanged = true;
    this.conceitoDropdownAberto = false;
    this.clearAutoSaveTimer();
    this.emitSaveIfNeeded(true);
  }

  limparConceito(event: MouseEvent): void {
    this.selecionarConceito(null, event);
  }

  onClickParecer(): void {
    if (this.nota) {
      this.openParecer.emit(this.nota);
    }
  }

  onClickCha(): void {
    if (this.nota) {
      this.openCha.emit(this.nota);
    }
  }

  onClickAvaliacaoDescritiva(): void {
    if (this.nota) {
      this.openAvaliacaoDescritiva.emit(this.nota);
    }
  }

  isLeituraSomente(): boolean {
    const nota = this.nota;
    if (!nota) {
      return true;
    }
    const editavel = this.usarEditavelAnual ? nota.$$editavelAnual : nota.$$editavel;
    return this.disabled || !editavel || !!nota.$$isSaving;
  }

  isAcaoSecundariaDesabilitada(): boolean {
    const nota = this.nota;
    if (!nota) {
      return true;
    }

    return this.disabled || !!nota.$$isSaving;
  }

  podeMostrarParecer(): boolean {
    return !!(this.nota && this.mostrarParecer && !this.isModoDescritivo());
  }

  podeMostrarCha(): boolean {
    if (!this.nota || !this.mostrarCha || !this.nota.$$permiteCha) {
      return false;
    }
    if (!this.bloquearChaExameFinal) {
      return true;
    }
    return this.nota.avaliavel?.tipoPeriodo !== 'EXAME_FINAL';
  }

  deveOcultarNotaExame(): boolean {
    const nota = this.nota;
    const tipo = String(nota?.avaliavel?.tipoPeriodo || '');
    const situacao = String(nota?.enturmacao?.situacao || nota?.enturmacao?.matricula?.situacao || '').toUpperCase();
    return tipo === 'NOTA_PARA_EXAME' && InputNotaComponent.TIPOS_OCULTA_NOTA_EXAME.includes(situacao);
  }

  private prepararNotaParaSalvar(formatarNumerico: boolean): boolean {
    const nota = this.nota;
    if (!nota) {
      return false;
    }

    if (this.isModoNumerico()) {
      const valorNormalizado = this.normalizarValorNumerico(String(nota.$$nota || ''));
      const mensagemErro = this.validarValorNumerico(valorNormalizado);
      if (mensagemErro) {
        nota.$$mensagemValidacao = mensagemErro;
        nota.$$hasChanged = false;
        return false;
      }
      if (formatarNumerico) {
        nota.$$nota = valorNormalizado
          ? this.formatarNotaNumericaConformeConfiguracao(valorNormalizado)
          : '';
      }
    }

    return true;
  }

  private scheduleAutoSave(): void {
    if (!this.nota || this.isLeituraSomente() || !this.nota.$$hasChanged) {
      return;
    }

    this.clearAutoSaveTimer();
    this.autoSaveTimer = setTimeout(() => {
      const salvou = this.emitSaveIfNeeded(false);
      if (salvou) {
        this.blurFocusedElementInsideComponent();
      }
    }, 1000);
  }

  private clearAutoSaveTimer(): void {
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  }

  private emitSaveIfNeeded(formatarNumerico: boolean): boolean {
    const nota = this.nota;
    if (!nota) {
      return false;
    }

    if (!this.prepararNotaParaSalvar(formatarNumerico)) {
      return false;
    }

    if (nota.$$hasChanged && !nota.$$isSaving) {
      this.notaSalva.emit(nota);
      nota.$$hasChanged = false;
      return true;
    }

    return false;
  }

  private blurFocusedElementInsideComponent(): void {
    const active = document.activeElement as HTMLElement | null;
    if (!active) {
      return;
    }

    if (this.hostRef.nativeElement.contains(active)) {
      active.blur();
    }
  }

  private normalizarValorNumerico(valor: string): string {
    const texto = String(valor || '').trim();
    if (!texto) {
      return '';
    }
    return texto.replace(',', '.');
  }

  private validarValorNumerico(valor: string): string | null {
    if (!valor) {
      return null;
    }

    if (!/^\d+([.,]\d+)?$/.test(valor)) {
      return 'Informe um valor numérico válido.';
    }

    const numeroDigitado = this.parseUnknownNumber(valor);
    const limites = this.getNotaLimites();

    if (numeroDigitado !== null) {
      if (limites.max !== null && numeroDigitado > limites.max) {
        return `Ops, o valor não pode ser maior que ${limites.max}.`;
      }
      if (limites.min !== null && numeroDigitado < limites.min) {
        return `Ops, o valor não pode ser menor que ${limites.min}.`;
      }
    }

    const casasDecimais = this.getQuantidadeCasasDecimais();
    const [inteiro, decimal = ''] = valor.replace(',', '.').split('.');
    if (!inteiro || (casasDecimais === 0 && decimal.length > 0)) {
      return casasDecimais === 0
        ? 'A configuração da turma permite somente números inteiros.'
        : `A nota deve ter no máximo ${casasDecimais} casa(s) decimal(is).`;
    }

    if (decimal.length > casasDecimais) {
      return `A nota deve ter no máximo ${casasDecimais} casa(s) decimal(is).`;
    }

    if (this.isDecimaisZeroOuCinco() && decimal.length > 0) {
      if (!this.isDecimalZeroOuCincoValido(decimal, casasDecimais)) {
        return this.buildMensagemDecimaisZeroOuCinco(casasDecimais);
      }
    }

    return null;
  }

  private getQuantidadeCasasDecimais(): number {
    const qtd = (this.configuracaoAvaliacao as Record<string, unknown>)['quantidadeDecimais'];
    if (qtd === 'DUAS') {
      return 2;
    }
    if (qtd === 'UMA') {
      return 1;
    }
    return 0;
  }

  private formatarNotaNumericaConformeConfiguracao(valor: string | number): string {
    const numero = this.parseUnknownNumber(valor);
    if (numero === null) {
      return String(valor ?? '');
    }
    const casasDecimais = this.getQuantidadeCasasDecimais();
    return numero.toFixed(casasDecimais).replace('.', ',');
  }

  private aplicarMascaraNumericaTempoReal(valor: string): string {
    const texto = String(valor || '');
    if (!texto.trim()) {
      return '';
    }

    const casasDecimais = this.getQuantidadeCasasDecimais();
    const limiteDigitos = this.getLimiteDigitosNumerico();
    const somenteDigitos = limiteDigitos
      ? texto.replace(/\D/g, '').slice(0, limiteDigitos)
      : texto.replace(/\D/g, '');
    if (!somenteDigitos) {
      return '';
    }

    if (casasDecimais <= 0) {
      return somenteDigitos;
    }

    if (somenteDigitos.length <= casasDecimais) {
      return somenteDigitos;
    }

    const parteInteira = somenteDigitos.slice(0, -casasDecimais);
    const parteDecimal = somenteDigitos.slice(-casasDecimais);
    return `${parteInteira},${parteDecimal}`;
  }

  private getLimiteDigitosNumerico(): number | null {
    const notaMaxima = this.getNotaLimites().max;
    if (notaMaxima === null) {
      return null;
    }
    const casasDecimais = this.getQuantidadeCasasDecimais();
    const limiteInteiro = Math.max(1, Math.trunc(Math.abs(notaMaxima)).toString().length);
    return limiteInteiro + casasDecimais;
  }

  private getNotaLimites(): { min: number | null; max: number | null } {
    let min: number | null = null;
    let max: number | null = null;
    
    const config = this.configuracaoAvaliacao as any;
    if (config) {
      if (config.avaliacaoNumerica) {
        min = this.parseUnknownNumber(config.avaliacaoNumerica.notaMinima) ?? this.parseUnknownNumber(config.notaMinima);
        max = this.parseUnknownNumber(config.avaliacaoNumerica.notaMaxima) ?? this.parseUnknownNumber(config.notaMaxima);
      } else {
        min = this.parseUnknownNumber(config.notaMinima);
        max = this.parseUnknownNumber(config.notaMaxima);
      }
    }

    const minAvaliavel = this.parseUnknownNumber((this.nota?.avaliavel as any)?.notaMinima);
    if (minAvaliavel !== null) min = minAvaliavel;

    const maxAvaliavel = this.parseUnknownNumber((this.nota?.avaliavel as any)?.notaMaxima);
    if (maxAvaliavel !== null) max = maxAvaliavel;

    return { min, max };
  }

  private isDecimaisZeroOuCinco(): boolean {
    const decimais = (this.configuracaoAvaliacao as Record<string, unknown>)['decimaisAvaliacao'];
    return decimais === 'ZERO_OU_CINCO';
  }

  private isDecimalZeroOuCincoValido(decimal: string, casasDecimais: number): boolean {
    const decimalNormalizado = decimal.padEnd(casasDecimais, '0');
    if (casasDecimais === 1) {
      return decimalNormalizado === '0' || decimalNormalizado === '5';
    }
    if (casasDecimais === 2) {
      return decimalNormalizado === '00' || decimalNormalizado === '50';
    }
    return true;
  }

  private buildMensagemDecimaisZeroOuCinco(casasDecimais: number): string {
    if (casasDecimais === 1) {
      return 'Use apenas decimal 0 ou 5.';
    }
    if (casasDecimais === 2) {
      return 'Use apenas decimais 00 ou 50.';
    }
    return 'Valor decimal inválido para esta configuração.';
  }

  private resolveModoAvaliacaoColuna(coluna: ColunaDesempenho | null | undefined): string | undefined {
    const rawValue = coluna?.rawValue as { modoAvaliacao?: unknown } | undefined;
    return typeof rawValue?.modoAvaliacao === 'string' ? String(rawValue.modoAvaliacao) : undefined;
  }

  private getConceitosNumericos(
    configuracao: Record<string, unknown>
  ): Array<Record<string, unknown>> {
    const avaliacaoConceitoNumerico = configuracao['avaliacaoConceitoNumerico'] as
      | { conceitos?: Array<Record<string, unknown>>; conceitosNumericos?: Array<Record<string, unknown>> }
      | undefined;

    if (
      Array.isArray(avaliacaoConceitoNumerico?.conceitos) &&
      avaliacaoConceitoNumerico!.conceitos!.length
    ) {
      return avaliacaoConceitoNumerico!.conceitos!;
    }
    return avaliacaoConceitoNumerico?.conceitosNumericos || [];
  }

  private resolveConceitoSigla(item: Record<string, unknown>): string {
    const sigla = item['sigla'] || item['abreviacao'] || item['abreviatura'] || item['acronimo'];
    return sigla ? String(sigla) : '';
  }

  private resolveConceitoDescricao(item: Record<string, unknown>): string | undefined {
    const descricao = item['descricao'] || item['nome'] || item['rotulo'] || item['label'];
    return descricao ? String(descricao) : undefined;
  }

  private buildConceitoFallbackSigla(value: unknown): string {
    const texto = String(value || '').trim();
    if (!texto) {
      return '';
    }
    const primeiraPalavra = texto.match(/[A-Za-zÀ-ÿ0-9]+/);
    const caracteres = primeiraPalavra?.[0] || texto;
    // Extrai até 2 caracteres, ou apenas 1 se não tiver 2
    return caracteres.slice(0, 2).toUpperCase();
  }

  private getSiglaAlfabeticaPorIndice(indice: number): string {
    return indice < 0 ? '' : String.fromCharCode(65 + indice);
  }

  getConceitoOptionSigla(
    conceito: { sigla?: string; descricao?: string } | string | null | undefined
  ): string {
    if (typeof conceito === 'string') {
      return this.buildConceitoFallbackSigla(conceito);
    }
    return String(conceito?.sigla || this.buildConceitoFallbackSigla(conceito?.descricao) || '');
  }

  private getConceitoSiglaSelecionada(): string {
    const nota = this.nota;
    if (!nota) {
      return '';
    }
    const opcoes = this.getConceitoOptions();
    if (!opcoes.length) {
      return '';
    }
    const valorSelecionado = this.getNotaConceitoValue();
    const siglaDireta = this.getConceitoSiglaCurta(
      valorSelecionado as { sigla?: string; descricao?: string } | null
    );
    if (siglaDireta) {
      return siglaDireta;
    }
    const chaveSelecionada = this.getConceitoOptionKey(valorSelecionado);
    const indiceSelecionado = opcoes.findIndex(
      (o) => !!chaveSelecionada && this.getConceitoOptionKey(o) === chaveSelecionada
    );
    if (indiceSelecionado >= 0) {
      return this.getConceitoSiglaCurta(opcoes[indiceSelecionado], indiceSelecionado);
    }
    const valorTexto =
      typeof nota.$$nota === 'string'
        ? nota.$$nota
        : typeof nota.notaConceito === 'string'
        ? nota.notaConceito
        : '';
    const indicePorTexto = opcoes.findIndex(
      (o) => o.descricao === valorTexto || o.sigla === valorTexto
    );
    return indicePorTexto >= 0
      ? this.getConceitoSiglaCurta(opcoes[indicePorTexto], indicePorTexto)
      : '';
  }

  private getTextoConceitoSelecionado(): unknown {
    const nota = this.nota;
    if (!nota) {
      return '';
    }
    const valorNota = nota.$$nota as Record<string, unknown> | string | null | undefined;
    if (valorNota && typeof valorNota === 'object') {
      return valorNota['sigla'] || valorNota['descricao'] || valorNota['label'] || valorNota['nome'] || '';
    }
    if (typeof valorNota === 'string' && valorNota.trim()) {
      return valorNota;
    }
    const notaConceito = nota.notaConceito as Record<string, unknown> | string | null | undefined;
    if (notaConceito && typeof notaConceito === 'object') {
      return (
        notaConceito['sigla'] ||
        notaConceito['descricao'] ||
        notaConceito['label'] ||
        notaConceito['nome'] ||
        ''
      );
    }
    if (typeof notaConceito === 'string' && notaConceito.trim()) {
      return notaConceito;
    }
    const conceitoSelecionado = this.getNotaConceitoValue() as {
      descricao?: string;
      sigla?: string;
    } | null;
    return conceitoSelecionado ? conceitoSelecionado.sigla || conceitoSelecionado.descricao || '' : '';
  }

  private extrairPrimeiraLetraConceito(valor: unknown): string {
    const texto = String(valor || '').trim();
    if (!texto) {
      return '';
    }
    const antesDoSeparador = texto.split(' - ')[0]?.trim() || texto;
    const match = antesDoSeparador.match(/[A-Za-zÀ-ÿ0-9]/g);
    if (!match || match.length === 0) {
      return '';
    }
    return match.slice(0, 2).join('').toUpperCase();
  }

  private getConceitoSiglaCurta(
    conceito: { sigla?: string; descricao?: string } | null | undefined,
    index?: number
  ): string {
    const sigla = String(conceito?.sigla || '').trim();
    if (sigla) {
      return this.buildConceitoFallbackSigla(sigla);
    }
    const descricao = String(conceito?.descricao || '').trim();
    if (descricao) {
      return this.buildConceitoFallbackSigla(descricao);
    }
    return (
      this.buildConceitoFallbackSigla(this.getConceitoOptionSigla(conceito)) ||
      this.getSiglaAlfabeticaPorIndice(index ?? -1)
    );
  }

  private getNotaConceitoValue(): Record<string, unknown> | null {
    const nota = this.nota;
    if (!nota) {
      return null;
    }
    if (nota.$$nota && typeof nota.$$nota === 'object') {
      return nota.$$nota as Record<string, unknown>;
    }
    const conceito = nota.notaConceito;
    if (conceito && typeof conceito === 'object') {
      return conceito as Record<string, unknown>;
    }

    if (this.getModoAvaliacao() === 'CONCEITO_NUMERICO') {
      const conceitos = this.getConceitoOptions();
      const valorNumerico = this.parseUnknownNumber(nota.$$nota ?? nota.notaNumerica);
      if (valorNumerico !== null) {
        const conceitoCorrespondente = this.getConceitoCorrespondente(conceitos, valorNumerico);
        if (conceitoCorrespondente) {
          return conceitoCorrespondente;
        }
      }
    }

    const sigla =
      typeof nota.$$nota === 'string'
        ? nota.$$nota
        : typeof conceito === 'string'
        ? conceito
        : '';
    return this.getConceitoOptions().find((item) => item.sigla === sigla || item.descricao === sigla) || null;
  }

  private getConceitoCorrespondente(
    conceitos: Array<Record<string, unknown>>,
    nota: number
  ): Record<string, unknown> | null {
    return (
      conceitos.find((conceito) => {
        const valorMinimo = this.parseUnknownNumber(conceito['valorMinimo']);
        const valorMaximo = this.parseUnknownNumber(conceito['valorMaximo']);
        if (valorMinimo === null || valorMaximo === null) {
          return false;
        }
        return nota >= valorMinimo && nota <= valorMaximo;
      }) || null
    );
  }

  private parseUnknownNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    const parsed = Number(String(value).replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }

  private getConceitoOptionKey(value: unknown): string {
    if (!value || typeof value !== 'object') {
      return String(value || '');
    }
    const option = value as { id?: unknown; sigla?: unknown; descricao?: unknown };
    if (option.id !== undefined && option.id !== null) {
      return String(option.id);
    }
    if (typeof option.sigla === 'string') {
      return option.sigla;
    }
    return String(option.descricao || '');
  }

  private normalizeNotaSelectValue(value: unknown): NotaView['$$nota'] {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      return value;
    }
    if (typeof value === 'object') {
      return value as Exclude<NotaView['$$nota'], string | number | null>;
    }
    return String(value);
  }
}
